import ic_arrow_counter_clockwise from '../../../../../../../assets/icons/ic_arrow_counter_clockwise.svg';
import ic_caret_down from '../../../../../../../assets/icons/ic_caret_down.svg';
import ic_caret_down_white from '../../../../../../../assets/icons/ic_caret_down_white.svg';
import ic_money from '../../../../../../../assets/icons/ic_money.svg';
import { useDictionary } from '../../../../../../../utilities/hooks/UseDictionary';
import CustomToast from '../../../../../common/CustomToast';
import Popover from '../../../../../common/Popover';
import {
	recordOfflineBookingPayment,
	recordOfflineBookingRefund
} from '../../../networking';
import PaymentItem from './PaymentItem';
import RecordTransactionPopover from './RecordTransactionPopover';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { UseCurrencyDecorator } from '../../../../../../../utilities/hooks/UseCurrencyDecorator';
import {AmountFormatHelper} from "../../../../../../../utilities/helpers/AmountFormatHelper";

// Matches the toast styling used elsewhere in the booking details screen.
// `kind` is toast.success / toast.error; `type` picks the CustomToast icon.
const showToast = (kind, message, type) =>
	kind(<CustomToast message={message} type={type} />, {
		position: 'bottom-left',
		autoClose: 5000,
		toastId: 'record-transaction',
		hideProgressBar: true,
		closeOnClick: true,
		pauseOnHover: false,
		draggable: false,
		progress: undefined,
		closeButton: false,
		icon: false,
		className: 'toast-white-warning'
	});

const Payments = ({ details, onRecorded }) => {
	const dictionary = useDictionary();
	const currencyDecorator = UseCurrencyDecorator();

	const totalPaid = useMemo(() => {
		return details.payments.reduce((s, c) => {
			return s + c.amount;
		}, 0);
	}, [details]);

	// Remaining = booking total - total paid (never negative). The booking
	// total lives on the invoice, falling back to the raw booking amount —
	// same source the record-payment popover uses.
	const remaining = useMemo(() => {
		const bookingTotal = details?.invoice?.total ?? details?.amount ?? 0;
		return Math.max(bookingTotal - totalPaid, 0);
	}, [details, totalPaid]);

	// Recording offline payments/refunds only applies to bookings accepted as
	// offline — same robust flag check used in BookingDetails.
	const acceptedAsOffline =
		details?.accepted_as_offline === true ||
		details?.accepted_as_offline === 1 ||
		details?.accepted_as_offline === '1';

	const paymentStatusKey = details?.payment_status?.key;
	const bookingStatusKey = details?.status?.key;

	//  - Add payment  → same gate as "Mark as paid": offline + payment
	//    pending + booking confirmed/reschedule (and still something owed).
	//  - Add refund   → offline + money actually paid on the booking, as long
	//    as no refund has been recorded yet. Keyed on `totalPaid` (not
	//    `payment_status === 'paid'`) so a partially paid booking that was
	//    cancelled can still be refunded. Once a refund lands the status
	//    becomes refunded / partial_refunded, which hides the button — by
	//    design only a single refund is supported.
	const canAddPayment =
		acceptedAsOffline &&
		paymentStatusKey === 'pending' &&
		(bookingStatusKey === 'confirmed' || bookingStatusKey === 'reschedule') &&
		remaining > 0;
	const canAddRefund =
		acceptedAsOffline &&
		totalPaid > 0 &&
		paymentStatusKey !== 'refunded' &&
		paymentStatusKey !== 'partial_refunded';

	const [openPopover, setOpenPopover] = useState(null);

	const togglePopover = (popover) =>
		setOpenPopover((current) => (current === popover ? null : popover));
	const closePopover = () => setOpenPopover(null);

	// Records the transaction, then refreshes the booking so the new payment
	// row and updated statuses appear. The promise is re-thrown on failure so
	// the popover stays open and the user can retry.
	const handleRecordPayment = async (payload) => {
		try {
			await recordOfflineBookingPayment(details.id, payload);
			closePopover();
			showToast(
				toast.success,
				dictionary.payment_recorded_successfully,
				'success'
			);
			onRecorded && onRecorded();
		} catch (error) {
			showToast(
				toast.error,
				error?.response?.data?.message || dictionary.problem_recording_payment,
				'warning'
			);
			throw error;
		}
	};

	const handleRecordRefund = async (payload) => {
		try {
			await recordOfflineBookingRefund(details.id, payload);
			closePopover();
			showToast(
				toast.success,
				dictionary.refund_recorded_successfully,
				'success'
			);
			onRecorded && onRecorded();
		} catch (error) {
			showToast(
				toast.error,
				error?.response?.data?.message || dictionary.problem_recording_refund,
				'warning'
			);
			throw error;
		}
	};

	return (
		<div className="detail-tab-container fade-on-load">
			<div
				className="participant-row-container payments-tab-header"
				style={{ justifyContent: 'space-between', marginBottom: '20px' }}
			>
				<h3
					className="booking-tab-header"
					data-testid="participant-groups-header"
				>
					{dictionary.payment_transaction}
				</h3>
				<div className="payments-tab-actions">
					{canAddPayment && (
						<Popover
							open={openPopover === 'payment'}
							onClose={closePopover}
							testId="record-payment-popover"
							trigger={
								<div
									className="button plus-button payment-action-button"
									data-testid="add-payment-button"
									onClick={() => togglePopover('payment')}
								>
									{dictionary.add_payment}
									<img
										src={ic_caret_down_white}
										alt=""
										className="payment-action-caret"
									/>
								</div>
							}
						>
							<RecordTransactionPopover
								mode="payment"
								details={details}
								onClose={closePopover}
								onSubmit={handleRecordPayment}
							/>
						</Popover>
					)}
					{canAddRefund && (
						<Popover
							open={openPopover === 'refund'}
							onClose={closePopover}
							testId="record-refund-popover"
							trigger={
								<div
									className="secondary-button payment-action-button"
									data-testid="add-refund-button"
									onClick={() => togglePopover('refund')}
								>
									<img
										src={ic_arrow_counter_clockwise}
										alt=""
										style={{ width: '16px', height: '16px' }}
									/>
									{dictionary.add_refund}
									<img
										src={ic_caret_down}
										alt=""
										className="payment-action-caret"
									/>
								</div>
							}
						>
							<RecordTransactionPopover
								mode="refund"
								details={details}
								onClose={closePopover}
								onSubmit={handleRecordRefund}
							/>
						</Popover>
					)}
				</div>
			</div>
			{details?.payments?.length > 0 ? (
				<div className="detail-payments-table-container thin-scrollbar">
					<table
						className="detail-payments-table"
						data-testid="payments-table"
						cellSpacing="0"
						cellPadding="5"
						border="0"
					>
						<thead>
							<tr>
								<td>#</td>
								<td>{dictionary.date}</td>
								<td>{dictionary.type}</td>
								<td>{dictionary.reference}</td>
								<td>{dictionary.card_number}</td>
								<td align="right">{dictionary.amount}</td>
							</tr>
						</thead>
						<tbody>
							{details?.payments?.map((item) => (
								<PaymentItem key={item.id} item={item} currency={details?.currency} />
							))}
							<tr>
								<td colSpan={5} align="right">
									{dictionary.total_paid}
								</td>
								<td align="right">
									<b>{currencyDecorator(AmountFormatHelper.formatAmount(totalPaid), details?.currency)}</b>
								</td>
							</tr>
							<tr>
								<td colSpan={5} align="right">
									{dictionary.remaining}
								</td>
								<td align="right">
									<b>{currencyDecorator(AmountFormatHelper.formatAmount(remaining), details?.currency)}</b>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			) : (
				<div
					className="empty-participants-wrapper"
					data-testid="payments-empty-state"
				>
					<div className="empty-participants payments-empty-participants">
						<div className="empty-participants-icon-wrapper">
							<img src={ic_money} alt="" />
						</div>
						<h5 className="empty-participants-title">
							{dictionary.no_payment_transactions_yet}
						</h5>
						<p className="empty-participants-description">
							{dictionary.all_payment_transactions_will_be_listed}
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

Payments.propTypes = {
	details: PropTypes.object.isRequired,
	onRecorded: PropTypes.func
};

Payments.defaultProps = {
	onRecorded: null
};

export default Payments;
